import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  Timestamp as FirestoreTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  MeasurementDefinition,
  TemplateMeasurementConfig,
  MeasurementReading,
  MeasurementSummary,
  TrendAnalysis,
  MeasurementValidationResult,
  MeasurementQueryOptions,
  BatchMeasurementInput,
  BatchMeasurementResult,
  MeasurementExportOptions,
} from '@easydone/shared';

// Helper to convert Timestamp to millis
function timestampToMillis(ts: any): number {
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts === 'number') return ts;
  if (ts && typeof ts.toMillis === 'function') return ts.toMillis();
  if (ts && typeof ts.seconds === 'number') return ts.seconds * 1000;
  return Date.now();
}

// Helper to convert Timestamp to Date
function timestampToDate(ts: any): Date {
  if (ts instanceof Date) return ts;
  if (typeof ts === 'number') return new Date(ts);
  if (ts && typeof ts.toDate === 'function') return ts.toDate();
  if (ts && typeof ts.seconds === 'number') return new Date(ts.seconds * 1000);
  return new Date();
}

/**
 * Measurement Definition Management
 */

export async function getMeasurementDefinitions(
  _orgId: string,
  _context?: 'floc' | 'asset'
): Promise<MeasurementDefinition[]> {
  // Note: Measurement definitions are stored in template settings
  // This function would query templates and extract unique measurement definitions
  // For now, return empty array - will be populated from templates
  return [];
}

/**
 * Measurement Reading Operations
 */

export async function recordMeasurement(
  orgId: string,
  reading: Omit<MeasurementReading, 'id' | 'timestamp' | 'createdAt'>
): Promise<string> {
  const now = FirestoreTimestamp.now();

  const measurementData: Omit<MeasurementReading, 'id'> = {
    ...reading,
    timestamp: now,
    createdAt: now,
    numericValue: typeof reading.value === 'number' ? reading.value : undefined,
  };

  const docRef = await addDoc(
    collection(db, 'organizations', orgId, 'measurements'),
    measurementData
  );

  // Update summary asynchronously
  updateMeasurementSummary(orgId, reading).catch(console.error);

  return docRef.id;
}

export async function recordBatchMeasurements(
  orgId: string,
  input: BatchMeasurementInput
): Promise<BatchMeasurementResult> {
  const result: BatchMeasurementResult = {
    success: true,
    successCount: 0,
    errorCount: 0,
    readingIds: [],
    errors: [],
  };

  const batch = writeBatch(db);
  const now = FirestoreTimestamp.now();

  for (let i = 0; i < input.measurements.length; i++) {
    const reading = input.measurements[i];

    try {
      // Validate if required
      if (input.validateAll) {
        // Validation would go here
        // const validation = await validateReading(reading);
        // if (!validation.valid && input.stopOnError) { ... }
      }

      const docRef = doc(collection(db, 'organizations', orgId, 'measurements'));
      const measurementData: Omit<MeasurementReading, 'id'> = {
        ...reading,
        timestamp: now,
        createdAt: now,
        numericValue: typeof reading.value === 'number' ? reading.value : undefined,
      };

      batch.set(docRef, measurementData);
      result.readingIds.push(docRef.id);
      result.successCount++;
    } catch (error: any) {
      result.errorCount++;
      result.errors.push({
        index: i,
        error: error.message || 'Unknown error',
      });

      if (input.stopOnError) {
        result.success = false;
        break;
      }
    }
  }

  if (result.successCount > 0) {
    await batch.commit();
  }

  return result;
}

export async function getMeasurementReading(
  orgId: string,
  id: string
): Promise<MeasurementReading | null> {
  const docRef = doc(db, 'organizations', orgId, 'measurements', id);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return { id: docSnap.id, ...docSnap.data() } as MeasurementReading;
}

export async function getLatestMeasurementReading(
  orgId: string,
  entityType: 'asset' | 'floc',
  entityId: string,
  measurementDefId: string
): Promise<MeasurementReading | null> {
  const measurementsRef = collection(db, 'organizations', orgId, 'measurements');

  const entityField = entityType === 'asset' ? 'assetId' : 'flocId';

  const q = query(
    measurementsRef,
    where(entityField, '==', entityId),
    where('measurementDefId', '==', measurementDefId),
    orderBy('readingDate', 'desc'),
    firestoreLimit(1)
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const docSnap = snapshot.docs[0];
  return { id: docSnap.id, ...docSnap.data() } as MeasurementReading;
}

export async function updateMeasurementReading(
  orgId: string,
  id: string,
  updates: Partial<MeasurementReading>
): Promise<void> {
  const docRef = doc(db, 'organizations', orgId, 'measurements', id);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: FirestoreTimestamp.now(),
  });
}

export async function deleteMeasurementReading(
  orgId: string,
  id: string
): Promise<void> {
  const docRef = doc(db, 'organizations', orgId, 'measurements', id);
  await deleteDoc(docRef);
}

/**
 * Measurement Query Operations
 */

export async function getMeasurementHistory(
  orgId: string,
  entityType: 'floc' | 'asset',
  entityId: string,
  measurementDefId: string,
  options: MeasurementQueryOptions = {}
): Promise<MeasurementReading[]> {
  const constraints: any[] = [
    where('measurementDefId', '==', measurementDefId),
  ];

  if (entityType === 'floc') {
    constraints.push(where('flocId', '==', entityId));
  } else {
    constraints.push(where('assetId', '==', entityId));
  }

  if (options.startDate) {
    constraints.push(
      where('readingDate', '>=', FirestoreTimestamp.fromDate(options.startDate))
    );
  }

  if (options.endDate) {
    constraints.push(
      where('readingDate', '<=', FirestoreTimestamp.fromDate(options.endDate))
    );
  }

  constraints.push(
    orderBy('readingDate', options.sortOrder === 'asc' ? 'asc' : 'desc')
  );

  if (options.limit) {
    constraints.push(firestoreLimit(options.limit));
  }

  const q = query(collection(db, 'organizations', orgId, 'measurements'), ...constraints);
  const snapshot = await getDocs(q);

  const readings = snapshot.docs.map(
    (doc) => ({ id: doc.id, ...doc.data() } as MeasurementReading)
  );

  // Apply aggregation if requested
  if (options.aggregation && options.aggregation !== 'raw') {
    return aggregateReadings(readings, options.aggregation);
  }

  return readings;
}

export async function getMeasurementsForEntity(
  orgId: string,
  entityType: 'floc' | 'asset',
  entityId: string,
  options: MeasurementQueryOptions = {}
): Promise<MeasurementReading[]> {
  const constraints: any[] = [];

  if (entityType === 'floc') {
    constraints.push(where('flocId', '==', entityId));
  } else {
    constraints.push(where('assetId', '==', entityId));
  }

  if (options.startDate) {
    constraints.push(
      where('readingDate', '>=', FirestoreTimestamp.fromDate(options.startDate))
    );
  }

  if (options.endDate) {
    constraints.push(
      where('readingDate', '<=', FirestoreTimestamp.fromDate(options.endDate))
    );
  }

  constraints.push(orderBy('readingDate', 'desc'));

  if (options.limit) {
    constraints.push(firestoreLimit(options.limit));
  }

  const q = query(collection(db, 'organizations', orgId, 'measurements'), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map(
    (doc) => ({ id: doc.id, ...doc.data() } as MeasurementReading)
  );
}

export async function getLatestMeasurements(
  orgId: string,
  entityType: 'floc' | 'asset',
  entityId: string
): Promise<Map<string, MeasurementReading>> {
  const readings = await getMeasurementsForEntity(orgId, entityType, entityId, {
    limit: 100,
  });

  // Group by measurement definition and get latest
  const latestByDef = new Map<string, MeasurementReading>();

  for (const reading of readings) {
    const existing = latestByDef.get(reading.measurementDefId);
    if (
      !existing ||
      timestampToMillis(reading.readingDate) > timestampToMillis(existing.readingDate)
    ) {
      latestByDef.set(reading.measurementDefId, reading);
    }
  }

  return latestByDef;
}

/**
 * Measurement Summary Operations
 */

async function updateMeasurementSummary(
  orgId: string,
  reading: Partial<MeasurementReading>
): Promise<void> {
  if (!reading.measurementDefId) return;

  const entityType = reading.flocId ? 'floc' : reading.assetId ? 'asset' : null;
  if (!entityType) return;

  const entityId = reading.flocId || reading.assetId!;
  const summaryId = `${entityType}_${entityId}_${reading.measurementDefId}`;

  // Get recent readings for statistics (last 30 days)
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const recentReadings = await getMeasurementHistory(
    orgId,
    entityType,
    entityId,
    reading.measurementDefId,
    {
      startDate: thirtyDaysAgo,
      limit: 1000,
    }
  );

  // Calculate statistics
  const numericReadings = recentReadings
    .filter((r) => typeof r.value === 'number')
    .map((r) => r.value as number);

  const stats = calculateStatistics(numericReadings);
  const trend = detectTrend(recentReadings);

  // Count threshold breaches
  const warningCount = recentReadings.filter(
    (r) => r.status === 'warning'
  ).length;
  const criticalCount = recentReadings.filter(
    (r) => r.status === 'critical'
  ).length;

  // Get latest reading
  const latest = recentReadings[0];

  const summary: Omit<MeasurementSummary, 'id'> = {
    measurementDefId: reading.measurementDefId,
    measurementName: reading.measurementName || '',
    unit: reading.unit,
    category: reading.measurementCategory,
    entityType,
    entityId,
    entityName:
      (entityType === 'floc' ? reading.flocName : reading.assetName) || '',
    latestValue: (latest?.value ?? reading.value) as string | number | boolean,
    latestTimestamp: latest?.readingDate || FirestoreTimestamp.now(),
    latestStatus: latest?.status || reading.status || 'normal',
    latestReadingId: latest?.id || '',
    stats: {
      ...stats,
      trend: trend.direction,
      trendPercentage: trend.changePercent,
      trendPeriodDays: 30,
    },
    warningCount,
    criticalCount,
    totalReadings: recentReadings.length,
    lastUpdated: FirestoreTimestamp.now(),
    stale: false,
  };

  const summaryRef = doc(db, 'organizations', orgId, 'measurementSummaries', summaryId);
  await setDoc(summaryRef, summary, { merge: true });
}

export async function getMeasurementSummary(
  orgId: string,
  entityType: 'floc' | 'asset',
  entityId: string
): Promise<MeasurementSummary[]> {
  const q = query(
    collection(db, 'organizations', orgId, 'measurementSummaries'),
    where('entityType', '==', entityType),
    where('entityId', '==', entityId)
  );

  const snapshot = await getDocs(q);
  const summaries = snapshot.docs.map(
    (doc) => ({ id: doc.id, ...doc.data() } as MeasurementSummary)
  );

  // Deduplicate: keep only the most recent summary for each measurementDefId
  const deduped = new Map<string, MeasurementSummary>();
  for (const summary of summaries) {
    const existing = deduped.get(summary.measurementDefId);
    if (!existing || timestampToMillis(summary.lastUpdated) > timestampToMillis(existing.lastUpdated)) {
      deduped.set(summary.measurementDefId, summary);
    }
  }

  return Array.from(deduped.values());
}

/**
 * Trend Analysis
 */

export async function calculateTrend(
  orgId: string,
  entityType: 'floc' | 'asset',
  entityId: string,
  measurementDefId: string,
  periodDays: number = 30
): Promise<TrendAnalysis> {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - periodDays);

  const readings = await getMeasurementHistory(
    orgId,
    entityType,
    entityId,
    measurementDefId,
    { startDate, endDate, sortOrder: 'asc' }
  );

  // Extract numeric data points
  const dataPoints = readings
    .filter((r) => typeof r.value === 'number')
    .map((r) => ({
      timestamp: timestampToDate(r.readingDate),
      value: r.value as number,
      status: r.status,
    }));

  // Calculate statistics
  const values = dataPoints.map((d) => d.value);
  const baseStats = calculateStatistics(values);
  const statistics = {
    count: baseStats.count,
    avg: baseStats.avg ?? 0,
    min: baseStats.min ?? 0,
    max: baseStats.max ?? 0,
    stdDev: baseStats.stdDev ?? 0,
    median: baseStats.median ?? 0,
    firstValue: values[0] ?? 0,
    lastValue: values[values.length - 1] ?? 0,
  };

  // Detect trend
  const trend = detectTrend(readings);

  // Calculate linear regression
  const regression = linearRegression(dataPoints);

  // Threshold analysis
  const thresholds = {
    warning: readings[0]?.measurementDefId
      ? undefined
      : { min: undefined, max: undefined },
    critical: readings[0]?.measurementDefId
      ? undefined
      : { min: undefined, max: undefined },
    breachCount:
      readings.filter((r) => r.status === 'warning' || r.status === 'critical')
        .length,
    warningBreachCount: readings.filter((r) => r.status === 'warning').length,
    criticalBreachCount: readings.filter((r) => r.status === 'critical')
      .length,
  };

  // Simple forecast (linear projection)
  const forecast =
    regression.slope !== undefined
      ? {
          nextValue: statistics.lastValue + regression.slope * 7, // 7 days ahead
          nextDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
          confidence: regression.rSquared || 0,
        }
      : undefined;

  return {
    measurementDefId,
    measurementName: readings[0]?.measurementName || '',
    entityType,
    entityId,
    startDate,
    endDate,
    periodDays,
    dataPoints,
    statistics,
    trend: {
      ...trend,
      slope: regression.slope,
      rSquared: regression.rSquared,
    },
    thresholds,
    forecast,
  };
}

/**
 * Validation
 */

export async function validateReading(
  reading: Partial<MeasurementReading>,
  definition: TemplateMeasurementConfig
): Promise<MeasurementValidationResult> {
  const errors: string[] = [];
  const warnings: string[] = [];
  let status: 'normal' | 'warning' | 'critical' = 'normal';

  // Type validation
  if (definition.dataType === 'number' && typeof reading.value !== 'number') {
    errors.push('Value must be a number');
  }

  // Numeric validations
  if (typeof reading.value === 'number') {
    if (definition.minValue !== undefined && reading.value < definition.minValue) {
      errors.push(`Value must be >= ${definition.minValue}`);
    }

    if (definition.maxValue !== undefined && reading.value > definition.maxValue) {
      errors.push(`Value must be <= ${definition.maxValue}`);
    }

    // Threshold checks
    if (definition.criticalThreshold) {
      if (
        (definition.criticalThreshold.min !== undefined &&
          reading.value < definition.criticalThreshold.min) ||
        (definition.criticalThreshold.max !== undefined &&
          reading.value > definition.criticalThreshold.max)
      ) {
        status = 'critical';
        warnings.push('Value exceeds critical threshold');
      }
    }

    if (status === 'normal' && definition.warningThreshold) {
      if (
        (definition.warningThreshold.min !== undefined &&
          reading.value < definition.warningThreshold.min) ||
        (definition.warningThreshold.max !== undefined &&
          reading.value > definition.warningThreshold.max)
      ) {
        status = 'warning';
        warnings.push('Value exceeds warning threshold');
      }
    }
  }

  // Select validation
  if (definition.dataType === 'select' && definition.options) {
    if (!definition.options.includes(String(reading.value))) {
      errors.push('Value must be one of the predefined options');
    }
  }

  // Required field validation
  if (definition.required && !reading.value) {
    errors.push('This measurement is required');
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    status,
  };
}

/**
 * Export Operations
 */

export async function exportMeasurements(
  options: MeasurementExportOptions
): Promise<any> {
  // Build query based on filters
  const constraints: any[] = [];

  if (options.startDate) {
    constraints.push(
      where('readingDate', '>=', FirestoreTimestamp.fromDate(options.startDate))
    );
  }

  if (options.endDate) {
    constraints.push(
      where('readingDate', '<=', FirestoreTimestamp.fromDate(options.endDate))
    );
  }

  if (options.measurementDefIds && options.measurementDefIds.length > 0) {
    // Firestore 'in' query limited to 10 items
    if (options.measurementDefIds.length <= 10) {
      constraints.push(where('measurementDefId', 'in', options.measurementDefIds));
    }
  }

  constraints.push(orderBy('readingDate', 'asc'));

  const q = query(collection(db, 'organizations', options.orgId, 'measurements'), ...constraints);
  const snapshot = await getDocs(q);

  let readings = snapshot.docs.map(
    (doc) => ({ id: doc.id, ...doc.data() } as MeasurementReading)
  );

  // Additional filtering for fields not supported by Firestore queries
  if (options.flocIds && options.flocIds.length > 0) {
    readings = readings.filter((r) => r.flocId && options.flocIds!.includes(r.flocId));
  }

  if (options.assetIds && options.assetIds.length > 0) {
    readings = readings.filter((r) => r.assetId && options.assetIds!.includes(r.assetId));
  }

  if (options.categories && options.categories.length > 0) {
    readings = readings.filter(
      (r) => r.measurementCategory && options.categories!.includes(r.measurementCategory)
    );
  }

  // Apply aggregation if requested
  if (options.aggregation && options.aggregation !== 'raw') {
    readings = aggregateReadings(readings, options.aggregation);
  }

  // Format based on export format
  switch (options.format) {
    case 'csv':
      return formatAsCSV(readings, options.includeMetadata);
    case 'json':
      return formatAsJSON(readings, options.includeMetadata);
    case 'parquet':
      // Parquet would require additional library
      throw new Error('Parquet export not yet implemented');
    default:
      return readings;
  }
}

/**
 * Helper Functions
 */

function calculateStatistics(values: number[]): {
  avg?: number;
  min?: number;
  max?: number;
  stdDev?: number;
  median?: number;
  count: number;
} {
  if (values.length === 0) {
    return { count: 0 };
  }

  const sorted = [...values].sort((a, b) => a - b);
  const sum = values.reduce((acc, val) => acc + val, 0);
  const avg = sum / values.length;

  const variance =
    values.reduce((acc, val) => acc + Math.pow(val - avg, 2), 0) / values.length;
  const stdDev = Math.sqrt(variance);

  const median =
    values.length % 2 === 0
      ? (sorted[values.length / 2 - 1] + sorted[values.length / 2]) / 2
      : sorted[Math.floor(values.length / 2)];

  return {
    count: values.length,
    avg,
    min: sorted[0],
    max: sorted[sorted.length - 1],
    stdDev,
    median,
  };
}

function detectTrend(readings: MeasurementReading[]): {
  direction: 'increasing' | 'decreasing' | 'stable' | 'insufficient_data';
  confidence: number;
  changePercent: number;
  changeAbsolute: number;
} {
  if (readings.length < 2) {
    return {
      direction: 'insufficient_data',
      confidence: 0,
      changePercent: 0,
      changeAbsolute: 0,
    };
  }

  const numericReadings = readings.filter((r) => typeof r.value === 'number');

  if (numericReadings.length < 2) {
    return {
      direction: 'insufficient_data',
      confidence: 0,
      changePercent: 0,
      changeAbsolute: 0,
    };
  }

  // Simple trend: compare first half to second half
  const midpoint = Math.floor(numericReadings.length / 2);
  const firstHalf = numericReadings.slice(0, midpoint);
  const secondHalf = numericReadings.slice(midpoint);

  const firstAvg =
    firstHalf.reduce((acc, r) => acc + (r.value as number), 0) / firstHalf.length;
  const secondAvg =
    secondHalf.reduce((acc, r) => acc + (r.value as number), 0) /
    secondHalf.length;

  const changeAbsolute = secondAvg - firstAvg;
  const changePercent = firstAvg !== 0 ? (changeAbsolute / firstAvg) * 100 : 0;

  // Determine direction with 5% threshold for "stable"
  let direction: 'increasing' | 'decreasing' | 'stable' = 'stable';
  if (Math.abs(changePercent) > 5) {
    direction = changePercent > 0 ? 'increasing' : 'decreasing';
  }

  // Confidence based on data points and consistency
  const confidence = Math.min(numericReadings.length / 10, 1);

  return {
    direction,
    confidence,
    changePercent,
    changeAbsolute,
  };
}

function linearRegression(
  dataPoints: { timestamp: Date; value: number }[]
): {
  slope?: number;
  intercept?: number;
  rSquared?: number;
} {
  if (dataPoints.length < 2) {
    return {};
  }

  // Convert timestamps to x values (days since first reading)
  const firstTime = dataPoints[0].timestamp.getTime();
  const points = dataPoints.map((p) => ({
    x: (p.timestamp.getTime() - firstTime) / (24 * 60 * 60 * 1000),
    y: p.value,
  }));

  const n = points.length;
  const sumX = points.reduce((acc, p) => acc + p.x, 0);
  const sumY = points.reduce((acc, p) => acc + p.y, 0);
  const sumXY = points.reduce((acc, p) => acc + p.x * p.y, 0);
  const sumX2 = points.reduce((acc, p) => acc + p.x * p.x, 0);

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  // Calculate R-squared
  const yMean = sumY / n;
  const ssTotal = points.reduce((acc, p) => acc + Math.pow(p.y - yMean, 2), 0);
  const ssResidual = points.reduce(
    (acc, p) => acc + Math.pow(p.y - (slope * p.x + intercept), 2),
    0
  );
  const rSquared = 1 - ssResidual / ssTotal;

  return { slope, intercept, rSquared };
}

function aggregateReadings(
  readings: MeasurementReading[],
  aggregation: 'hourly' | 'daily' | 'weekly' | 'monthly'
): MeasurementReading[] {
  // Group readings by time period
  const groups = new Map<string, MeasurementReading[]>();

  readings.forEach((reading) => {
    const date = timestampToDate(reading.readingDate);
    let key: string;

    switch (aggregation) {
      case 'hourly':
        key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}`;
        break;
      case 'daily':
        key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
        break;
      case 'weekly':
        const weekNum = Math.floor(date.getDate() / 7);
        key = `${date.getFullYear()}-${date.getMonth()}-W${weekNum}`;
        break;
      case 'monthly':
        key = `${date.getFullYear()}-${date.getMonth()}`;
        break;
    }

    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(reading);
  });

  // Aggregate each group
  const aggregated: MeasurementReading[] = [];

  groups.forEach((groupReadings) => {
    const numericValues = groupReadings
      .filter((r) => typeof r.value === 'number')
      .map((r) => r.value as number);

    if (numericValues.length === 0) {
      aggregated.push(groupReadings[0]);
      return;
    }

    const avgValue =
      numericValues.reduce((acc, val) => acc + val, 0) / numericValues.length;

    // Use first reading as template
    const template = groupReadings[0];

    aggregated.push({
      ...template,
      value: avgValue,
      numericValue: avgValue,
      notes: `Aggregated from ${groupReadings.length} readings`,
    });
  });

  return aggregated;
}

function formatAsCSV(
  readings: MeasurementReading[],
  includeMetadata: boolean
): string {
  if (readings.length === 0) return '';

  const headers = [
    'ID',
    'Measurement',
    'Value',
    'Unit',
    'FLOC',
    'Asset',
    'Date',
    'Source',
    'Recorded By',
    'Status',
    'Quality',
  ];

  if (includeMetadata) {
    headers.push('Notes', 'Tags', 'Category');
  }

  const rows = readings.map((r) => {
    const row = [
      r.id,
      r.measurementName,
      String(r.value),
      r.unit || '',
      r.flocName || '',
      r.assetName || '',
      timestampToDate(r.readingDate).toISOString(),
      r.source,
      r.recordedByName,
      r.status || '',
      r.quality,
    ];

    if (includeMetadata) {
      row.push(r.notes || '', (r.tags || []).join(';'), r.measurementCategory || '');
    }

    return row.join(',');
  });

  return [headers.join(','), ...rows].join('\n');
}

function formatAsJSON(
  readings: MeasurementReading[],
  includeMetadata: boolean
): string {
  const data = includeMetadata
    ? readings
    : readings.map((r) => ({
        id: r.id,
        measurement: r.measurementName,
        value: r.value,
        unit: r.unit,
        floc: r.flocName,
        asset: r.assetName,
        date: timestampToDate(r.readingDate).toISOString(),
        status: r.status,
      }));

  return JSON.stringify(data, null, 2);
}
